import type { SubscriptionOwner } from './subscription.js';

/**
 * 구독에 쓴 `applicationServerKey` 와 그 구독을 맡긴 서버를 기억한다 (WB-14).
 *
 * ── 왜 이것을 저장해야 하는가 (절대 규칙 19) ──────────────────
 *
 * 브라우저의 `PushSubscription` 은 **어느 키로 만들어졌는지 알려 주지 않는다.**
 * `getSubscription()` 이 주는 것은 endpoint 와 두 키(p256dh·auth)뿐이고, 그것으로
 * `applicationServerKey` 를 되돌릴 수 없다.
 *
 * 그래서 서버가 VAPID 를 갈거나 모드가 바뀌면 **앱은 자기 구독이 이미 죽었다는 것을
 * 알 방법이 없다.** 알림만 조용히 오지 않는다 — 권한은 `granted` 이고, 구독 객체도
 * 멀쩡히 있고, 화면은 「알림 켜짐」이다. 발송이 401/403 으로 실패하는 것은 서버 로그에만
 * 남는다.
 *
 * 우리가 쓴 키를 우리가 적어 두는 것이 그 침묵을 깨는 유일한 방법이다.
 */
export const PUSH_STORAGE_KEYS = {
  /** 구독에 쓴 VAPID 공개키 */
  key: 'bloom.push.key',
  /** 그 구독을 맡긴 서버 (`fast` | `tv2m`). 모드 전환을 알아채는 근거다 (WB-16) */
  owner: 'bloom.push.owner',
} as const;

export interface StoredSubscriptionKey {
  key: string | null;
  owner: SubscriptionOwner | null;
}

export function readSubscribedKey(): StoredSubscriptionKey {
  return {
    key: read(PUSH_STORAGE_KEYS.key),
    owner: toOwner(read(PUSH_STORAGE_KEYS.owner)),
  };
}

export function writeSubscribedKey(key: string, owner: SubscriptionOwner): void {
  write(PUSH_STORAGE_KEYS.key, key);
  write(PUSH_STORAGE_KEYS.owner, owner);
}

/** 구독이 없어졌을 때 지운다. 남겨 두면 다음 부팅이 있지도 않은 구독을 재사용한다. */
export function clearSubscribedKey(): void {
  remove(PUSH_STORAGE_KEYS.key);
  remove(PUSH_STORAGE_KEYS.owner);
}

function toOwner(value: string | null): SubscriptionOwner | null {
  return value === 'fast' || value === 'tv2m' ? value : null;
}

function read(key: string): string | null {
  try {
    const value = window.localStorage.getItem(key);
    return value === '' ? null : value;
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* 프라이빗 모드. 대조할 근거를 잃을 뿐이고, 그때는 매 부팅이 재구독으로 판정된다 */
  }
}

function remove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* 위와 같다 */
  }
}
